import { FC, useEffect, useState } from 'react';
import { PlatformStatusIndicator } from '@/components/platform/PlatformStatusIndicator';
import { RefreshCw, Wifi } from 'lucide-react';
import api from '@/utils/api';
import { Tooltip } from '@/components/ui';

interface PlatformStatus {
    connected: boolean;
    accountName?: string | null;
    pageName?: string | null;
    username?: string | null;
}

interface ConnectionStatuses {
    linkedin: PlatformStatus;
    facebook: PlatformStatus;
    instagram: PlatformStatus;
    twitter: PlatformStatus;
}

export const PlatformConnectionsCard: FC = () => {
    const [statuses, setStatuses] = useState<ConnectionStatuses | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchStatuses = async (showRefreshing = false) => {
        try {
            if (showRefreshing) setRefreshing(true);
            else setLoading(true);
            const { data } = await api.get<ConnectionStatuses>('/users/connection-status');
            setStatuses(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchStatuses();
    }, []);

    const connectedCount = statuses
        ? Object.values(statuses).filter(s => s.connected).length
        : 0;

    if (loading) return (
        <div className="bg-white rounded-2xl p-6 border border-slate-100 min-h-[180px] flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <Wifi className="w-4 h-4 text-slate-400" />
                    <Tooltip content="Check which of your social media accounts are currently connected." position="bottom">
                        <div>
                            <h3 className="text-xs font-semibold text-slate-800">Connections</h3>
                            <p className="text-[10px] text-slate-400">{connectedCount}/4 active</p>
                        </div>
                    </Tooltip>
                </div>
                <button
                    onClick={() => fetchStatuses(true)}
                    disabled={refreshing}
                    className="p-1.5 hover:bg-slate-50 rounded-md transition-colors"
                    title="Refresh status"
                >
                    <RefreshCw className={`w-3 h-3 text-slate-300 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Platforms Grid */}
            <div className="p-4 grid grid-cols-2 gap-2">
                <PlatformStatusIndicator platform="linkedin" status={statuses?.linkedin || { connected: false }} />
                <PlatformStatusIndicator platform="facebook" status={statuses?.facebook || { connected: false }} />
                <PlatformStatusIndicator platform="instagram" status={statuses?.instagram || { connected: false }} />
                <PlatformStatusIndicator platform="twitter" status={statuses?.twitter || { connected: false }} />
            </div>
        </div>
    );
};
